import React, { useState } from 'react';
import { CheckIcon, LockIcon, ShieldAlertIcon, XIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { naira } from '../../utils/format';
import { statusTone } from '../../utils/services';
import { providerServiceQueue } from '../../data/services';
import type { RequestStatus } from '../../types/services';

/**
 * The provider side of Academic Services. A provider sees only the fields the
 * student granted for that specific request — nothing else about them.
 */
export function ProviderServiceQueue() {
  const [queue, setQueue] = useState(providerServiceQueue.map(item => ({
    ...item,
    status: item.status as RequestStatus
  })));
  function setStatus(requestId: string, status: RequestStatus, pendingAction: string) {
    setQueue(current => current.map(item => item.requestId === requestId ? {
      ...item,
      status,
      pendingAction
    } : item));
  }
  return <div className="space-y-4" data-id="element-4186" data-mp="1uhgb51roccu0">
      <Panel title="Academic service requests" description="Requests students have sent you. Accept only what you can genuinely complete." data-id="element-4187" data-mp="1ugep6294e5xi">
        <ul className="space-y-3" data-id="element-4188" data-mp="23i4ijdmkbexx">
          {queue.map(item => <li key={item.requestId} className="rounded-card border border-line-strong bg-surface px-4 py-3.5" data-id="element-4189" data-mp="23fs43ag0jmpm">
              <div className="flex flex-wrap items-center gap-2" data-id="element-4190" data-mp="1bqox3mwqry9a">
                <p className="text-[14px] font-semibold text-ink" data-id="element-4191" data-mp="1bsjit3fu7uef">{item.service}</p>
                <Badge tone={statusTone(item.status)} data-id="element-4192" data-mp="1ft3h4ac5d425">{item.status}</Badge>
                <span className="cz-num ml-auto text-[12px] text-ink-subtle" data-id="element-4193" data-mp="1kr4x2mea0n9e">{item.requestId}</span>
              </div>
              <p className="mt-0.5 text-[12px] text-ink-muted" data-id="element-4194" data-mp="1pivcv7vzg387">
                {item.student} · due {item.deadline} · your fee {naira(item.fee)}
              </p>

              <p className="mt-2 rounded-lg border border-line bg-canvas px-3.5 py-2.5 text-[12px] leading-5 text-ink" data-id="element-4195" data-mp="x1j0m6pagm53">
                <span className="font-semibold" data-id="element-4196" data-mp="20bwwavsxcbpz">Next action: </span>
                {item.pendingAction}
              </p>

              <div className="mt-2.5 grid gap-3 sm:grid-cols-2" data-id="element-4197" data-mp="1vd3ptnc21kzb">
                <div data-id="element-4198" data-mp="2959twb1xo1ie">
                  <p className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-4199" data-mp="293qeemo3puho">
                    Shared with you
                  </p>
                  <ul className="mt-1 space-y-0.5" data-id="element-4200" data-mp="1hdki2vn5obnj">
                    {item.visibleFields.map(field => <li key={field} className="flex items-start gap-1.5 text-[12px] leading-5 text-ink" data-id="element-4201" data-mp="1cfak6da1qwy8">
                        <CheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ok-700" data-id="element-4202" data-mp="1qesf7adxr6g1" /> {field}
                      </li>)}
                  </ul>
                </div>
                <div data-id="element-4203" data-mp="31t5ys31nozq9">
                  <p className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-4204" data-mp="31sns0chdb343">
                    Withheld
                  </p>
                  <ul className="mt-1 space-y-0.5" data-id="element-4205" data-mp="35bmnm6vub73q">
                    {item.withheldFields.map(field => <li key={field} className="flex items-start gap-1.5 text-[12px] leading-5 text-ink-muted" data-id="element-4206" data-mp="39cyiaqtkmiis">
                        <LockIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" data-id="element-4207" data-mp="30cifljknjfez" /> {field}
                      </li>)}
                  </ul>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap gap-2" data-id="element-4208" data-mp="3o4gv6fr4vwxx">
                {item.status === 'Awaiting provider' ? <>
                    <Button size="sm" variant="primary" icon={<CheckIcon className="h-3.5 w-3.5" data-id="element-4210" data-mp="3jacn1t64tiv9" />} onClick={() => setStatus(item.requestId, 'Accepted', 'Ask the student for their signed authorisation')} data-id="element-4209" data-mp="3ed2w2933gz5b">
                      Accept
                    </Button>
                    <Button size="sm" variant="danger" icon={<XIcon className="h-3.5 w-3.5" data-id="element-4212" data-mp="2j2pnrfdenac2" />} onClick={() => setStatus(item.requestId, 'Rejected', 'Declined — the student has been told')} data-id="element-4211" data-mp="2n6notvmaiv7r">
                      Decline
                    </Button>
                  </> : <>
                    <Button size="sm" variant="secondary" onClick={() => setStatus(item.requestId, 'Awaiting information', 'Waiting on the student to reply')} data-id="element-4213" data-mp="36vxz2u39onyh">
                      Request information
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => setStatus(item.requestId, 'Ready for collection', 'Collect from the Registry and upload proof')} data-id="element-4214" data-mp="3pl4n8wmty7ro">
                      Mark ready for collection
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => setStatus(item.requestId, 'Collected', 'Arrange delivery or hand over')} data-id="element-4215" data-mp="awxfy03xz2bm">
                      Record collection
                    </Button>
                  </>}
              </div>
            </li>)}
        </ul>
      </Panel>

      <Panel title="What you may not do" data-id="element-4216" data-mp="tlu2fht3kqja">
        <ul className="space-y-2" data-id="element-4217" data-mp="tozqqutwjody">
          {['Present yourself as an agent, officer or representative of any university.', 'Use an institution’s name, crest or letterhead in your listing.', 'Ask a student for their university username, password or one-time code.', 'Promise an outcome, a grade change, or faster institutional processing.', 'Act beyond the single task named in the student’s written authorisation.'].map(rule => <li key={rule} className="flex items-start gap-2 text-[12px] leading-5 text-ink-muted" data-id="element-4218" data-mp="33q2vrdnidx2f">
              <ShieldAlertIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warn-700" data-id="element-4219" data-mp="3iez2n7dtx41g" />
              {rule}
            </li>)}
        </ul>
        <p className="mt-2.5 text-[12px] leading-5 text-ink-muted" data-id="element-4220" data-mp="3df2y9717zald">
          Breaching any of these removes your verified status. Earnings for assisted requests settle
          after the student confirms delivery, through the payout rules in your earnings section.
        </p>
      </Panel>
    </div>;
}